/**
 * UI strings. The UI language is independent of the reading (message) language.
 * Keep strings short, sentence case, and in the voice of the interface.
 */
import { useStore } from './store.ts';
import { pickUiLocale } from '@tc/domain';

const en = {
  appName: 'T-Lián',
  signIn: 'Sign in',
  signingIn: 'Signing in…',
  email: 'Email',
  password: 'Password',
  newPassword: 'New password',
  currentPassword: 'Current password',
  passwordHint: 'At least 10 characters.',
  invalidCredentials: 'Email or password is wrong.',
  accountLocked: 'Too many attempts. Wait 15 minutes and try again.',
  networkError: 'Can’t reach the server. Check your connection and try again.',
  inviteOnly: 'Accounts are created by invitation. Ask your administrator for a link.',
  forgotPassword: 'Forgot password?',
  forgotTitle: 'Forgot password',
  forgotIntro: 'Enter the e-mail address you sign in with and we will help you choose a new password.',
  forgotSend: 'Send',
  forgotSentEmail: 'If the address has an account, an e-mail with a link is on its way. The link works for one hour. Check your spam folder too.',
  forgotSentAdmin: 'Your administrator has been told. You will get a link to choose a new password from them.',
  backToSignIn: 'Back to sign in',
  or: 'or',
  mfa: 'Two-step sign-in',
  mfaIntro: 'After your password you also enter a 6-digit code from an app on your phone (Google Authenticator, Microsoft Authenticator or the iPhone Passwords app).',
  mfaAdminTip: 'Recommended for administrators.',
  mfaTurnOn: 'Turn on two-step sign-in',
  mfaTurnOff: 'Turn off',
  mfaOn: 'On',
  mfaScan: 'Scan the code with the app on your phone, then type the 6 digits it shows.',
  mfaQrAlt: 'QR code for the authenticator app',
  mfaManual: 'Cannot scan? Enter this key in the app:',
  mfaCode: 'Code from the app',
  mfaConfirm: 'Confirm',
  mfaCodesTitle: 'Recovery codes',
  mfaCodesHint: 'Each code works once if you do not have your phone. Save them somewhere safe (e.g. your password manager). They are shown only now.',
  mfaCodesSaved: 'I have saved them',
  mfaCodesLeft: '{n} recovery codes left',
  mfaTitle: 'Two-step sign-in',
  mfaPrompt: 'Enter the 6-digit code from the authenticator app on your phone.',
  mfaRecoveryHint: 'No phone? Enter one of your recovery codes instead.',
  mfaCodeInvalid: 'The code is not right. Wait for the next code in the app and try again.',
  mfaTicketInvalid: 'That took too long or too many tries. Sign in again.',
  mfaTag: 'two-step',
  mfaAdminReset: 'Turn off two-step',
  mfaAdminResetConfirm: 'Turn off two-step sign-in for {name}? Do this only if they lost their phone and recovery codes.',
  desktopNotify: 'Show notifications on this computer',
  chatFiles: 'Files',
  chatFilesNone: 'No images or PDFs shared in this chat yet.',
  showInChat: 'Show in chat',
  noOneToAdd: 'Everyone you can add is already in this group.',
  noOneToAddAdmin: 'Everyone in T-Lián is already in this group. Invite more people under Administration → Invitations.',
  signInPasskey: 'Sign in with a passkey',
  passkeyInvalid: 'The passkey could not be used. Sign in with your password, then add the passkey again in Settings.',
  passkeys: 'Passkeys',
  passkeysIntro: 'Sign in with Face ID, Touch ID, Windows Hello or your phone instead of typing a password. Your password still works.',
  addPasskey: 'Add a passkey on this device',
  faceIdSignIn: 'Sign in with Face ID',
  faceIdIntro: 'Sign in and unlock the app with Face ID instead of typing your password. Your password still works.',
  faceIdAdd: 'Turn on Face ID sign-in',
  faceIdToggle: 'Sign in with Face ID on this iPhone',
  faceIdOffer: 'Use Face ID to sign in?',
  faceIdYes: 'Turn on',
  bioTitle: 'Fingerprint or face unlock',
  bioIntro: 'Sign in and unlock the app with your fingerprint or face instead of typing your password. Your password still works.',
  bioToggle: 'Sign in with fingerprint or face on this phone',
  bioSignIn: 'Sign in with fingerprint',
  bioOffer: 'Use your fingerprint or face to sign in?',
  faceIdNo: 'No thanks',
  faceIdRevoked: 'Face ID sign-in was turned off (signed out everywhere). Sign in with your password and turn it on again in Settings.',
  faceIdFailed: 'Face ID could not be turned on. Try again.',
  faceIdOfferHint: 'Then the app also locks when it has been closed for 5 minutes, and opens with a glance or a finger. You can change this any time in Settings → Security.',
  lockAfter: 'Lock the app after',
  lockNow: 'Immediately',
  lockMinutes: '{n} minutes',
  lockHour: '1 hour',
  lockNever: 'Never',
  lockTitle: 'T-Lián is locked',
  versionApp: 'App',
  versionWeb: 'web',
  lockUnlockFace: 'Unlock with Face ID',
  lockUnlockBio: 'Unlock with fingerprint',
  lockUsePassword: 'Sign in with password instead',
  removePasskey: 'Remove',
  passkeyLastUsed: 'Last used {when}',
  passkeyNever: 'Not used yet',
  passkeySynced: 'synced',
  passkeysUnsupported: 'This browser does not support passkeys.',
  passkeysDesktop: 'Passkeys work in the web browser. In the T-Lián app, sign in with your password.',
  resetRequested: 'Asked for a new password',
  joinTitle: 'Join {org}',
  joinIntro: 'Choose how you want to read messages. You can change this later.',
  displayName: 'Your name',
  uiLanguage: 'App language',
  nativeLanguage: 'Native language',
  nativeLanguageHint: 'The app is shown in this language, and every message is translated into it.',
  change: 'Change',
  readingLanguage: 'Read messages in',
  readingLanguageHint: 'Messages written in other languages are translated into this one. The original is always one click away.',
  createAccount: 'Create account',
  inviteInvalid: 'This invitation link has expired or was already used.',
  resetTitle: 'Choose a new password',
  resetDone: 'Password changed. Sign in with your new password.',
  resetInvalid: 'This reset link has expired or was already used.',
  savePassword: 'Save password',
  conversations: 'Chats',
  newChat: 'New chat',
  noConversations: 'No chats yet. Start one with a colleague or supplier.',
  noResults: 'No messages match.',
  selectConversation: 'Pick a chat on the left, or start a new one.',
  directMessage: 'Direct message',
  group: 'Group',
  groupName: 'Group name',
  addPeople: 'Add people',
  createGroup: 'Create group',
  startChat: 'Start chat',
  filterPeople: 'Find people',
  members: '{n} members',
  you: 'You',
  loadEarlier: 'Load earlier messages',
  beginning: 'This is the beginning of the chat.',
  typeMessage: 'Write a message',
  send: 'Send',
  sending: 'Sending…',
  notSent: 'Not sent',
  retry: 'Retry',
  discard: 'Discard',
  reply: 'Reply',
  react: 'Add reaction',
  edit: 'Edit',
  delete: 'Delete',
  copy: 'Copy text',
  copied: 'Copied',
  cancel: 'Cancel',
  save: 'Save',
  close: 'Close',
  replyingTo: 'Replying to {name}',
  editing: 'Editing message',
  edited: 'edited',
  deleted: 'Message deleted',
  confirmDelete: 'Delete this message for everyone?',
  writingIn: 'Writing in',
  autoDetect: 'Detect automatically',
  showOriginal: 'Show original',
  showTranslation: 'Show translation',
  translatedFrom: 'Translated from {lang}',
  originalIn: 'Original, {lang}',
  translating: 'Translating…',
  translationRetrying: 'Translation delayed, retrying…',
  translationFailed: 'Translation failed',
  translationDisabled: 'Translation is turned off',
  budgetExceeded: 'Translation limit reached for today',
  retranslate: 'Translate again',
  review: 'Check this translation',
  askClarification: 'Ask for clarification',
  clarifyGeneric: 'Could you confirm what you mean here? I want to be sure I understood correctly.',
  mockNotice: 'Demo mode: translations are simulated, not real.',
  flag_number_missing: 'A number in the original is missing: {d}',
  flag_number_added: 'A number appears that is not in the original: {d}',
  flag_code_missing: 'A code or part number was changed: {d}',
  flag_url_missing: 'A link or address was changed: {d}',
  flag_emoji_mismatch: 'Emoji differ from the original',
  flag_negation_missing: 'A “not” in the original may be lost',
  flag_negation_added: 'The translation may add a “not”',
  flag_condition_missing: 'An “if” condition may be lost',
  flag_weekday_mismatch: 'Weekday differs from the original',
  flag_model_uncertain: 'The translator found this unclear: {d}',
  detectedLanguage: 'Language detected automatically',
  mixedLanguage: 'Mixed languages',
  connConnecting: 'Connecting…',
  connPolling: 'Connected in compatibility mode. Messages may arrive a few seconds late.',
  connOffline: 'Offline. Messages you write will be sent when the connection returns.',
  settings: 'Settings',
  profile: 'Profile',
  enterToSend: 'Press Enter to send (Shift+Enter or Alt/Option+Enter for a new line)',
  changePassword: 'Change password',
  passwordChanged: 'Password changed. Other devices were signed out.',
  exportData: 'Download my data',
  exportHint: 'Your profile, chats you are in, and every message you wrote, as JSON.',
  signOut: 'Sign out',
  signOutEverywhere: 'Sign out on all devices',
  saved: 'Saved',
  groupInfo: 'Group details',
  chatInfo: 'Chat details',
  rename: 'Rename',
  leaveGroup: 'Leave group',
  confirmLeave: 'Leave this group? You will lose access to its messages.',
  remove: 'Remove',
  confirmRemove: 'Remove {name} from the group? They will lose access to its messages.',
  makeAdmin: 'Make admin',
  removeAdmin: 'Remove admin',
  role_owner: 'Owner',
  role_admin: 'Admin',
  role_member: 'Member',
  askHistory: 'May {name} see the earlier messages in this group?',
  historyOnlyNew: 'Only new messages',
  historyAll: 'The whole history',
  historyNote: 'People added later only see messages from the moment they join.',
  add: 'Add',
  admin: 'Administration',
  invites: 'Invitations',
  users: 'People',
  usage: 'Usage',
  costs: "Costs",
  costEstimateTitle: "AI usage (estimate)",
  costEstimateNote: "Estimated from list prices and what each call used. Shown also while the free quota covers it, so the real bill can be lower.",
  costToday: "Today",
  costThisMonth: "This month",
  costTrackedSince: "Counted since {date}",
  costNothingYet: "Nothing counted yet. Usage is counted from the first AI call after this update.",
  costByFeature: "Per feature",
  costByCompany: "Per company (who wrote or clicked)",
  costCalls: "Calls",
  costUsed: "Used",
  costUnpriced: "Some models have no price, so the sum is a lower bound.",
  costFtranslate: "Translation",
  costFcheck: "Spelling check",
  costFsummary: "Summaries",
  costFimage_text: "Text in images",
  costFtranscribe: "Voice input",
  costFtts: "Read aloud",
  costUnknownOrg: "Unknown",
  billTitle: "Alibaba Cloud bill",
  billNote: "What Alibaba has billed, all services (server, files, AI). Lags a few hours; usage covered by the free quota shows as 0.",
  billNotConfigured: "No billing key on the server. Set BILLING_ACCESS_KEY_ID and BILLING_ACCESS_KEY_SECRET (a RAM user with the policy AliyunBSSReadOnlyAccess) in .env.",
  billNoPermission: "The key may not read the bill. Give its RAM user the policy AliyunBSSReadOnlyAccess.",
  billError: "Could not fetch the bill ({code}).",
  billBalance: "Account balance",
  billRefresh: "Refresh",
  billTotal: "Total",
  billEmpty: "Nothing billed.",
  costChars: "characters",
  consentTitle: "Privacy and consent",
  consentIntro: "Before you continue, please read how your information is handled and give your consent. You can withdraw it at any time by deleting your account.",
  acceptAndContinue: "Accept and continue",
  declineSignOut: "Not now – sign out",
  privacy: "Privacy",
  privacyShow: "Show the privacy notice",
  deleteAccount: "Delete my account",
  deleteAccountHint: "All messages you have written will be emptied for everyone, you leave all chats, and your account is anonymised. This cannot be undone. Enter your password to confirm.",
  deleteAccountConfirm: "Delete my account permanently",
  glossaryStarterDone: "{added} terms added, {updated} earlier suggestions updated. Terms you edited yourself were left unchanged.",
  searchResults: "Search results",
  searchTooOld: "This message is too far back to open here.",
  showMore: "Show more",
  searchInChat: "Search in this chat",
  searchPlaceholder: "Search all chats (⌘K)",
  glossary: "Glossary",
  glossaryIntro: "Company terms used in every translation. The translator always uses the term you approve, and warns readers if it was not used. Names marked \"do not translate\" stay exactly as written.",
  glossaryNote: "Note",
  keepOriginal: "Do not translate (name or abbreviation)",
  keepOriginalShort: "Not translated",
  glossarySynonymsHint: "Separate several words for the same thing with ;  — the first one is used in translations, the others are also recognised.",
  glossarySearch: "Search terms",
  glossaryPaste: "Paste from Excel",
  glossaryPasteHint: "Copy rows from Excel/Numbers with the columns: Svenska, 简体中文, English, Note, Do not translate (x). Words for the same thing in one cell are separated by ;",
  glossaryImport: "Import {n} rows",
  glossaryStarter: "Add suggested AV terms",
  glossaryImported: "{added} terms added, {skipped} already existed.",
  glossaryEmpty: "No terms yet.",
  glossaryConfirmDelete: "Delete this term?",
  flag_glossary_term_missing: "A company term was not used: {d}",
  createInvite: 'Create invitation',
  organization: 'Company',
  newOrganization: 'New company…',
  newOrganizationName: 'Company name',
  emailOptional: 'Email (optional — locks the invite to this address)',
  asAdmin: 'Can administer the app',
  inviteSendEmail: 'Also e-mail the invitation, in',
  inviteMailed: 'The invitation was e-mailed to {to}.',
  inviteMailFailed: 'Could not e-mail {to}. Send the link below yourself.',
  micStart: 'Speak (click again when done)',
  micStop: 'Done',
  listening: 'Listening… speak now',
  transcribing: 'Turning speech into text…',
  sendWithAudio: 'Also send my voice ({d}) so the reader can listen',
  discardRecording: 'Remove recording',
  micDenied: 'The microphone is blocked. Allow it for this site in the browser (the icon in the address bar) and try again.',
  micMissing: 'No microphone found.',
  micFailed: 'Could not start the microphone.',
  speechFailed: 'Speech recognition failed. Try again or type the message.',
  speechEmpty: 'No speech was heard. Try again a little closer to the microphone.',
  speechBudget: 'The daily limit for speech has been reached. Type the message instead.',
  audioUploadFailed: 'The recording could not be uploaded. Check the connection, or remove the recording and send the text.',
  voiceMessage: 'Voice message',
  talkMode: 'Talk mode',
  talkModeHint: 'Click the mic and speak — it is sent at once. New messages are read aloud in your language.',
  talkModeOff: 'Turn off',
  noVoice: 'This device has no voice for {lang}; messages cannot be read aloud. Install one in the system speech settings.',
  readAloud: 'Read aloud',
  micHint: 'Click to start and stop – or hold to talk and release',
  releaseToSend: 'Release to finish · slide up to cancel',
  releaseToCancel: 'Release to cancel',
  activeNow: 'active now',
  lastSeen: 'last active {time}',
  nActive: '{n} active',
  typingOne: '{name} is typing…',
  typingMany: '{name} are typing…',
  listen: 'Listen',
  talkJoin: 'Turn on talk mode for me too',
  talkShort: 'Talk',
  searchShort: 'Search',
  mentionSomeone: 'Mention someone',
  mentionedYou: 'Mentioned you',
  pin: 'Pin',
  unpin: 'Unpin',
  pinned: 'Pinned',
  pinnedN: 'Pinned {i}/{n}',
  pinnedShowAll: 'Show all pinned',
  sys_pinned: '{actor} pinned a message.',
  sys_unpinned: '{actor} unpinned a message.',
  pushFailed: 'Push notifications could not be switched on (they do not work on some networks, e.g. Android in mainland China). Use e-mail reminders instead.',
  appearance: 'Appearance',
  themeSystem: 'Automatic',
  themeLight: 'Light',
  themeDark: 'Dark',
  themeHint: 'Automatic follows your phone or computer. Applies to this device.',
  soundNewMessages: 'Sound for new messages (on this device)',
  soundTest: 'Play',
  mixedTitle: 'Several companies in one chat',
  mixedBody: 'This chat would include people from {orgs}. They will see each other and every message in the chat.',
  mixedConfirm: 'Continue anyway',
  mixedAskAdmin: 'Only an administrator can create chats with several external companies. Ask Johan or Jens.',
  sys_talk_on: '{actor} turned on talk mode – messages are spoken and read aloud.',
  quote: 'Quote',
  imgTextButton: 'Translate the text in the image',
  imgTextTitle: 'Text in the image',
  imgTextWorking: 'Reading the image…',
  imgTextNone: 'No readable text was found in the image.',
  imgTextFailed: 'The image could not be read. Try again.',
  summarise: 'Summarise',
  sumChatTitle: 'Summary of the chat',
  sumDocTitle: 'Summary of {name}',
  sumWorking: 'Reading and summarising…',
  sumDecisions: 'Agreed / decided',
  sumDates: 'Dates and deadlines',
  sumCommitments: 'Who does what',
  sumOpen: 'Open questions',
  sumTruncated: 'Only the most recent part was included.',
  sumDisclaimer: 'Made by AI from the original texts — check important details in the chat or document.',
  sumNothing: 'There is nothing to summarise yet.',
  sumNoText: 'This PDF contains no text (it is probably scanned images), so it cannot be summarised yet.',
  sumUnreadable: 'This PDF could not be read.',
  sumUnavailable: 'Summaries are not available right now.',
  sumFailed: 'The summary could not be made. Try again.',
  sumPdf: 'Summarise in my language',
  receiptSent: 'Sent',
  receiptRead: 'Read',
  receiptReadAll: 'Read by everyone',
  receiptReadBy: 'Read by {n} of {total}',
  diag: 'Connection tests',
  diagIntro: 'Results from the connection test page. Send this link to a tester (no account needed):',
  diagEmpty: 'No tests yet.',
  diagCode: 'Code',
  diagWho: 'Who / device',
  diagFailures: 'Failed steps',
  didYouMean: 'Did you mean:',
  sendCorrected: 'Send corrected',
  sendAsIs: 'Send as written',
  editText: 'Edit',
  autoCorrected: 'Corrected before sending: {list}',
  textCheckTitle: 'Spelling check before sending',
  textCheckAsk: 'Ask me when something looks wrong (recommended)',
  textCheckAuto: 'Correct automatically',
  textCheckOff: 'Off',
  textCheckHint: 'Clear typos are caught before translation, so the reader gets the right meaning. Numbers, part numbers and names are never changed.',
  notifications: 'Notifications',
  pushOn: 'Turn on notifications on this device',
  pushOff: 'Turn off on this device',
  pushIsOn: 'You get a notification when a new message arrives and the app is not open.',
  pushIsOff: 'Off on this device.',
  pushNeedsInstall: 'On iPhone/iPad: first add the app to the home screen (Share → Add to Home Screen), open it from there, then turn on notifications here.',
  pushDenied: 'Notifications are blocked for this site. Allow them in the browser or system settings.',
  pushUnsupported: 'This browser cannot show notifications when the app is closed. E-mail reminders still work.',
  emailReminders: 'E-mail me when messages stay unread (no message text in the e-mail)',
  emailNotConfigured: 'E-mail reminders are not set up on the server yet.',
  notificationsHint: 'Notifications never contain message text — only who wrote and in which chat. In mainland China, notifications on Android phones may not arrive; e-mail reminders work there.',
  suggestions: 'Suggestions',
  suggestShort: 'Better translation?',
  suggestTitle: 'Suggest a better translation',
  suggestYourVersion: 'Your translation',
  suggestComment: 'Comment (optional)',
  suggestPrivacy: 'This message and your suggestion will be shown to the administrators, who decide whether to use it.',
  suggestSend: 'Send suggestion',
  suggestThanks: 'Thank you! An administrator will review your suggestion. Approved suggestions improve future translations of similar messages.',
  suggestFailed: 'Could not send the suggestion.',
  suggestionsIntro: 'Corrections suggested by readers. Approved ones are used as reference translations for similar messages in the future. For a single word or term, add it to the glossary instead.',
  suggestApply: 'Also replace the translation in the chat when approving',
  suggestionsEmpty: 'No suggestions waiting.',
  suggestMachine: 'Machine translation',
  suggestProposed: 'Suggested (you can edit before approving)',
  approve: 'Approve',
  reject: 'Reject',
  attach: 'Attach image, screenshot or PDF (you can also paste or drag)',
  attachments: 'Attachments',
  uploading: 'Uploading…',
  uploadFailed: 'Upload failed. Check the connection and try again.',
  fileTypeNotAllowed: 'Only images (PNG, JPEG, WebP, GIF) and PDF files can be sent.',
  tooManyFiles: 'At most {n} files at a time.',
  fileTooLarge: 'A file is larger than {size} and was skipped.',
  download: 'Download',
  openImage: 'Open image',
  adminNotAllowed: 'This address may not be an administrator. Only the addresses set by the server owner can be (enter the e-mail above).',
  emailTaken: 'There is already an account with this e-mail.',
  inviteFailed: 'Could not create the invite.',
  inviteCreated: 'Invitation created. Send this link to the person through a channel you trust. It works once and expires {date}.',
  copyLink: 'Copy link',
  inviteQr: 'QR code for the invitation',
  inviteLanguage: 'Invitation text in',
  inviteText: 'Invitation text',
  share: 'Share…',
  copyText: 'Copy text',
  copyQr: 'Copy QR image',
  downloadQr: 'Download QR',
  copyImageFailed: 'This browser cannot copy images — use Download QR.',
  inviteShareHint: 'WeChat: send the text, or the QR image (long-press → scan), or let them scan it from your screen.',
  revoke: 'Revoke',
  status_used: 'Used by {name}',
  status_revoked: 'Revoked',
  status_expired: 'Expired',
  status_open: 'Open until {date}',
  disable: 'Disable',
  enable: 'Enable',
  disabled: 'Disabled',
  resetLink: 'Password reset link',
  resetCreated: 'Reset link (works once, 24 hours):',
  todayChars: 'Characters translated today',
  monthCap: 'Monthly cap',
  dailyCap: 'Daily cap',
  queue: 'Job queue',
  noPrivateAccess: 'Administrators manage people and costs. They cannot read other people’s chats.',
  sys_group_created: '{actor} created the group',
  sys_renamed: '{actor} renamed the group to “{title}”',
  sys_members_added: '{actor} added {users}',
  sys_member_removed: '{actor} removed {users}',
  sys_member_left: '{actor} left the group',
  someone: 'Someone',
  error_generic: 'Something went wrong ({code}).',
  emoji_smileys: 'Smileys',
  emoji_gestures: 'Gestures',
  emoji_work: 'Work',
  emoji_symbols: 'Symbols',
  emoji_flags: 'Flags',
  next: 'Next',
  back: 'Back',
  more: 'More',
  settingsChat: 'Language and writing',
  settingsSecurity: 'Sign-in and security',
  settingsPrivacy: 'Privacy and data',
  dlShort: 'Apps',
  dlNone: 'No apps have been uploaded yet.',
  downloadApp: 'Download the app',
  dlTitle: 'Apps for computer and phone',
  dlIntro: 'The same chat as here, with notifications even when the window is closed.',
  dlMacArm: 'Mac (Apple chip, M1 and later)',
  dlMacIntel: 'Mac (Intel)',
  dlWindowsHint: 'If Windows says "Windows protected your PC": click More info → Run anyway.',
  dlAndroidHint: 'Open the file on the phone and allow installing from that app once.',
  dlIphoneHint: 'Through TestFlight: ask your administrator for an invitation.',
  dlButton: 'Download',
  later: 'Later',
  appUpdateReady: 'A new version of T-Lián ({v}) is ready.',
  appUpdateRestart: 'Restart and update',
  appUpdate: 'A new version of the app is available ({v}). Download it and install it over the old one.',
  dlForThis: 'For this device',
  dlOthers: 'Other versions',
  dlWinArm: 'Windows (ARM)',
  dlWinArmHint: 'Only for Windows laptops with an ARM chip (e.g. Snapdragon). Most PCs need the one above.',
  takePhoto: 'Take photo',
};

export type MsgKey = keyof typeof en;
type Dict = Record<MsgKey, string>;

const sv: Dict = {
  appName: 'T-Lián',
  signIn: 'Logga in',
  signingIn: 'Loggar in…',
  email: 'E-post',
  password: 'Lösenord',
  newPassword: 'Nytt lösenord',
  currentPassword: 'Nuvarande lösenord',
  passwordHint: 'Minst 10 tecken.',
  invalidCredentials: 'Fel e-post eller lösenord.',
  accountLocked: 'För många försök. Vänta 15 minuter och försök igen.',
  networkError: 'Når inte servern. Kontrollera anslutningen och försök igen.',
  inviteOnly: 'Konton skapas via inbjudan. Be din administratör om en länk.',
  forgotPassword: 'Glömt lösenordet?',
  forgotTitle: 'Glömt lösenordet',
  forgotIntro: 'Skriv e-postadressen du loggar in med så hjälper vi dig att välja ett nytt lösenord.',
  forgotSend: 'Skicka',
  forgotSentEmail: 'Om adressen har ett konto är ett mejl med en länk på väg. Länken gäller en timme. Titta även i skräpposten.',
  forgotSentAdmin: 'Administratören har fått besked. Du får en länk för att välja nytt lösenord av hen.',
  backToSignIn: 'Tillbaka till inloggning',
  or: 'eller',
  mfa: 'Tvåstegsinloggning',
  mfaIntro: 'Efter lösenordet anger du också en sexsiffrig kod från en app i telefonen (Google Authenticator, Microsoft Authenticator eller iPhones Lösenord-app).',
  mfaAdminTip: 'Rekommenderas för administratörer.',
  mfaTurnOn: 'Slå på tvåstegsinloggning',
  mfaTurnOff: 'Stäng av',
  mfaOn: 'På',
  mfaScan: 'Skanna koden med appen i telefonen och skriv sedan de sex siffror som visas.',
  mfaQrAlt: 'QR-kod för kodappen',
  mfaManual: 'Går det inte att skanna? Skriv in den här nyckeln i appen:',
  mfaCode: 'Kod från appen',
  mfaConfirm: 'Bekräfta',
  mfaCodesTitle: 'Reservkoder',
  mfaCodesHint: 'Varje kod fungerar en gång om du inte har telefonen till hands. Spara dem på ett säkert ställe, t.ex. i din lösenordshanterare. De visas bara nu.',
  mfaCodesSaved: 'Jag har sparat dem',
  mfaCodesLeft: '{n} reservkoder kvar',
  mfaTitle: 'Tvåstegsinloggning',
  mfaPrompt: 'Skriv den sexsiffriga koden från kodappen i telefonen.',
  mfaRecoveryHint: 'Har du inte telefonen? Skriv en av dina reservkoder i stället.',
  mfaCodeInvalid: 'Koden stämmer inte. Vänta på nästa kod i appen och försök igen.',
  mfaTicketInvalid: 'Det tog för lång tid eller för många försök. Logga in igen.',
  mfaTag: 'tvåsteg',
  mfaAdminReset: 'Stäng av tvåsteg',
  mfaAdminResetConfirm: 'Stänga av tvåstegsinloggning för {name}? Gör det bara om personen har tappat bort både telefonen och reservkoderna.',
  desktopNotify: 'Visa notiser på den här datorn',
  chatFiles: 'Filer',
  chatFilesNone: 'Inga bilder eller PDF:er har delats i den här chatten än.',
  showInChat: 'Visa i chatten',
  noOneToAdd: 'Alla du kan lägga till är redan med i gruppen.',
  noOneToAddAdmin: 'Alla i T-Lián är redan med i gruppen. Bjud in fler under Administration → Inbjudningar.',
  signInPasskey: 'Logga in med passkey',
  passkeyInvalid: 'Passkeyn kunde inte användas. Logga in med lösenord och lägg till passkeyn igen under Inställningar.',
  passkeys: 'Passkeys',
  passkeysIntro: 'Logga in med Face ID, Touch ID, Windows Hello eller telefonen i stället för att skriva lösenord. Lösenordet fungerar fortfarande.',
  addPasskey: 'Lägg till passkey på den här enheten',
  faceIdSignIn: 'Logga in med Face ID',
  faceIdIntro: 'Logga in och lås upp appen med Face ID i stället för att skriva lösenord. Lösenordet fungerar fortfarande.',
  faceIdAdd: 'Slå på inloggning med Face ID',
  faceIdToggle: 'Logga in med Face ID på den här iPhonen',
  faceIdOffer: 'Vill du använda Face ID för att logga in?',
  faceIdYes: 'Slå på',
  bioTitle: 'Fingeravtryck eller ansikte',
  bioIntro: 'Logga in och lås upp appen med fingeravtryck eller ansikte i stället för att skriva lösenord. Lösenordet fungerar fortfarande.',
  bioToggle: 'Logga in med fingeravtryck eller ansikte på den här telefonen',
  bioSignIn: 'Logga in med fingeravtryck',
  bioOffer: 'Vill du använda fingeravtryck eller ansikte för att logga in?',
  faceIdNo: 'Nej tack',
  faceIdRevoked: 'Inloggning med Face ID har stängts av (utloggad överallt). Logga in med lösenord och slå på det igen under Inställningar.',
  faceIdFailed: 'Face ID kunde inte slås på. Försök igen.',
  faceIdOfferHint: 'Då låses appen också när den varit stängd i 5 minuter och öppnas med en blick eller ett finger. Du kan ändra det när som helst under Inställningar → Säkerhet.',
  lockAfter: 'Lås appen efter',
  lockNow: 'Direkt',
  lockMinutes: '{n} minuter',
  lockHour: '1 timme',
  lockNever: 'Aldrig',
  lockTitle: 'T-Lián är låst',
  versionApp: 'App',
  versionWeb: 'webb',
  lockUnlockFace: 'Lås upp med Face ID',
  lockUnlockBio: 'Lås upp med fingeravtryck',
  lockUsePassword: 'Logga in med lösenord i stället',
  removePasskey: 'Ta bort',
  passkeyLastUsed: 'Senast använd {when}',
  passkeyNever: 'Inte använd än',
  passkeySynced: 'synkad',
  passkeysUnsupported: 'Den här webbläsaren stöder inte passkeys.',
  passkeysDesktop: 'Passkeys fungerar i webbläsaren. I T-Lián-appen loggar du in med lösenord.',
  resetRequested: 'Har bett om nytt lösenord',
  joinTitle: 'Gå med i {org}',
  joinIntro: 'Välj hur du vill läsa meddelanden. Du kan ändra det senare.',
  displayName: 'Ditt namn',
  uiLanguage: 'Appens språk',
  nativeLanguage: 'Modersmål',
  nativeLanguageHint: 'Appen visas på det här språket, och alla meddelanden översätts till det.',
  change: 'Ändra',
  readingLanguage: 'Läs meddelanden på',
  readingLanguageHint: 'Meddelanden på andra språk översätts till detta. Originalet finns alltid ett klick bort.',
  createAccount: 'Skapa konto',
  inviteInvalid: 'Inbjudningslänken har gått ut eller redan använts.',
  resetTitle: 'Välj ett nytt lösenord',
  resetDone: 'Lösenordet är ändrat. Logga in med det nya lösenordet.',
  resetInvalid: 'Återställningslänken har gått ut eller redan använts.',
  savePassword: 'Spara lösenord',
  conversations: 'Chattar',
  newChat: 'Ny chatt',
  noConversations: 'Inga chattar än. Starta en med en kollega eller leverantör.',
  noResults: 'Inga meddelanden matchar.',
  selectConversation: 'Välj en chatt till vänster, eller starta en ny.',
  directMessage: 'Direktmeddelande',
  group: 'Grupp',
  groupName: 'Gruppnamn',
  addPeople: 'Lägg till personer',
  createGroup: 'Skapa grupp',
  startChat: 'Starta chatt',
  filterPeople: 'Hitta personer',
  members: '{n} medlemmar',
  you: 'Du',
  loadEarlier: 'Visa tidigare meddelanden',
  beginning: 'Här börjar chatten.',
  typeMessage: 'Skriv ett meddelande',
  send: 'Skicka',
  sending: 'Skickar…',
  notSent: 'Inte skickat',
  retry: 'Försök igen',
  discard: 'Släng',
  reply: 'Svara',
  react: 'Lägg till reaktion',
  edit: 'Redigera',
  delete: 'Radera',
  copy: 'Kopiera text',
  copied: 'Kopierat',
  cancel: 'Avbryt',
  save: 'Spara',
  close: 'Stäng',
  replyingTo: 'Svarar {name}',
  editing: 'Redigerar meddelande',
  edited: 'redigerat',
  deleted: 'Meddelandet är raderat',
  confirmDelete: 'Radera meddelandet för alla?',
  writingIn: 'Skriver på',
  autoDetect: 'Känn igen automatiskt',
  showOriginal: 'Visa original',
  showTranslation: 'Visa översättning',
  translatedFrom: 'Översatt från {lang}',
  originalIn: 'Original, {lang}',
  translating: 'Översätter…',
  translationRetrying: 'Översättningen dröjer, försöker igen…',
  translationFailed: 'Översättningen misslyckades',
  translationDisabled: 'Översättning är avstängd',
  budgetExceeded: 'Dagens översättningsgräns är nådd',
  retranslate: 'Översätt igen',
  review: 'Kontrollera översättningen',
  askClarification: 'Be om förtydligande',
  clarifyGeneric: 'Kan du bekräfta vad du menar här? Jag vill vara säker på att jag har förstått rätt.',
  mockNotice: 'Demoläge: översättningarna är simulerade, inte riktiga.',
  flag_number_missing: 'Ett tal i originalet saknas: {d}',
  flag_number_added: 'Ett tal finns med som inte står i originalet: {d}',
  flag_code_missing: 'En kod eller ett artikelnummer har ändrats: {d}',
  flag_url_missing: 'En länk eller adress har ändrats: {d}',
  flag_emoji_mismatch: 'Emojierna skiljer sig från originalet',
  flag_negation_missing: 'Ett ”inte” i originalet kan ha försvunnit',
  flag_negation_added: 'Översättningen kan ha lagt till ett ”inte”',
  flag_condition_missing: 'Ett ”om”-villkor kan ha försvunnit',
  flag_weekday_mismatch: 'Veckodagen skiljer sig från originalet',
  flag_model_uncertain: 'Översättaren tyckte att detta var otydligt: {d}',
  detectedLanguage: 'Språket identifierades automatiskt',
  mixedLanguage: 'Blandade språk',
  connConnecting: 'Ansluter…',
  connPolling: 'Ansluten i kompatibilitetsläge. Meddelanden kan komma några sekunder sent.',
  connOffline: 'Frånkopplad. Det du skriver skickas när anslutningen är tillbaka.',
  settings: 'Inställningar',
  profile: 'Profil',
  enterToSend: 'Skicka med Enter (Skift+Enter eller Alt/Option+Enter för ny rad)',
  changePassword: 'Byt lösenord',
  passwordChanged: 'Lösenordet är bytt. Andra enheter loggades ut.',
  exportData: 'Ladda ner min data',
  exportHint: 'Din profil, dina chattar och alla meddelanden du skrivit, som JSON.',
  signOut: 'Logga ut',
  signOutEverywhere: 'Logga ut på alla enheter',
  saved: 'Sparat',
  groupInfo: 'Gruppinformation',
  chatInfo: 'Chattinformation',
  rename: 'Byt namn',
  leaveGroup: 'Lämna gruppen',
  confirmLeave: 'Lämna gruppen? Du förlorar åtkomst till dess meddelanden.',
  remove: 'Ta bort',
  confirmRemove: 'Ta bort {name} från gruppen? Personen förlorar åtkomst till meddelandena.',
  makeAdmin: 'Gör till admin',
  removeAdmin: 'Ta bort admin',
  role_owner: 'Ägare',
  role_admin: 'Admin',
  role_member: 'Medlem',
  askHistory: 'Får {name} se de tidigare meddelandena i gruppen?',
  historyOnlyNew: 'Bara nya meddelanden',
  historyAll: 'Hela historiken',
  historyNote: 'Personer som läggs till senare ser bara meddelanden från när de gick med.',
  add: 'Lägg till',
  admin: 'Administration',
  invites: 'Inbjudningar',
  users: 'Personer',
  usage: 'Användning',
  costs: "Kostnader",
  costEstimateTitle: "AI-användning (uppskattning)",
  costEstimateNote: "Uppskattat från listpris och vad varje anrop använde. Visas även när gratiskvoten täcker det, så den verkliga räkningen kan vara lägre.",
  costToday: "I dag",
  costThisMonth: "Den här månaden",
  costTrackedSince: "Räknas sedan {date}",
  costNothingYet: "Inget räknat än. Användningen räknas från första AI-anropet efter den här uppdateringen.",
  costByFeature: "Per funktion",
  costByCompany: "Per företag (den som skrev eller klickade)",
  costCalls: "Anrop",
  costUsed: "Förbrukat",
  costUnpriced: "Vissa modeller saknar pris, så summan är ett minimum.",
  costFtranslate: "Översättning",
  costFcheck: "Stavningskontroll",
  costFsummary: "Sammanfattningar",
  costFimage_text: "Text i bilder",
  costFtranscribe: "Röstinmatning",
  costFtts: "Uppläsning",
  costUnknownOrg: "Okänt",
  billTitle: "Alibaba Cloud-räkningen",
  billNote: "Det Alibaba har fakturerat, alla tjänster (server, filer, AI). Släpar några timmar; det gratiskvoten täcker visas som 0.",
  billNotConfigured: "Ingen faktureringsnyckel på servern. Lägg in BILLING_ACCESS_KEY_ID och BILLING_ACCESS_KEY_SECRET (en RAM-användare med policyn AliyunBSSReadOnlyAccess) i .env.",
  billNoPermission: "Nyckeln får inte läsa räkningen. Ge dess RAM-användare policyn AliyunBSSReadOnlyAccess.",
  billError: "Kunde inte hämta räkningen ({code}).",
  billBalance: "Saldo på kontot",
  billRefresh: "Uppdatera",
  billTotal: "Totalt",
  billEmpty: "Inget fakturerat.",
  costChars: "tecken",
  consentTitle: "Integritet och samtycke",
  consentIntro: "Innan du fortsätter: läs hur dina uppgifter hanteras och lämna ditt samtycke. Du kan återkalla det när som helst genom att radera ditt konto.",
  acceptAndContinue: "Godkänn och fortsätt",
  declineSignOut: "Inte nu – logga ut",
  privacy: "Integritet",
  privacyShow: "Visa integritetsinformationen",
  deleteAccount: "Radera mitt konto",
  deleteAccountHint: "Alla meddelanden du har skrivit töms för alla, du lämnar alla chattar och kontot anonymiseras. Det går inte att ångra. Ange ditt lösenord för att bekräfta.",
  deleteAccountConfirm: "Radera mitt konto permanent",
  glossaryStarterDone: "{added} termer tillagda, {updated} tidigare förslag uppdaterade. Termer du själv har ändrat lämnades orörda.",
  searchResults: "Sökresultat",
  searchTooOld: "Meddelandet ligger för långt bak för att öppnas här.",
  showMore: "Visa fler",
  searchInChat: "Sök i den här chatten",
  searchPlaceholder: "Sök i alla chattar (⌘K)",
  glossary: "Ordlista",
  glossaryIntro: "Företagets termer som används i alla översättningar. Översättaren använder alltid den term ni godkänt och varnar läsaren om den inte användes. Namn markerade \"översätt inte\" står kvar exakt som de är skrivna.",
  glossaryNote: "Anteckning",
  keepOriginal: "Översätt inte (namn eller förkortning)",
  keepOriginalShort: "Översätts inte",
  glossarySynonymsHint: "Skilj flera ord för samma sak med ;  — det första används i översättningar, de andra känns också igen.",
  glossarySearch: "Sök term",
  glossaryPaste: "Klistra in från Excel",
  glossaryPasteHint: "Kopiera rader från Excel/Numbers med kolumnerna: Svenska, 简体中文, English, Anteckning, Översätt inte (x). Flera ord för samma sak i en cell skiljs med ;",
  glossaryImport: "Importera {n} rader",
  glossaryStarter: "Lägg till föreslagna AV-termer",
  glossaryImported: "{added} termer tillagda, {skipped} fanns redan.",
  glossaryEmpty: "Inga termer än.",
  glossaryConfirmDelete: "Ta bort termen?",
  flag_glossary_term_missing: "En av företagets termer användes inte: {d}",
  createInvite: 'Skapa inbjudan',
  organization: 'Företag',
  newOrganization: 'Nytt företag…',
  newOrganizationName: 'Företagsnamn',
  emailOptional: 'E-post (valfritt — låser inbjudan till adressen)',
  asAdmin: 'Kan administrera appen',
  inviteSendEmail: 'Mejla också inbjudan, på',
  inviteMailed: 'Inbjudan har mejlats till {to}.',
  inviteMailFailed: 'Kunde inte mejla {to}. Skicka länken nedan själv.',
  micStart: 'Tala in (klicka igen när du är klar)',
  micStop: 'Klar',
  listening: 'Lyssnar… prata nu',
  transcribing: 'Gör om tal till text…',
  sendWithAudio: 'Skicka även min röst ({d}) så att mottagaren kan lyssna',
  discardRecording: 'Ta bort inspelningen',
  micDenied: 'Mikrofonen är blockerad. Tillåt den för den här sidan i webbläsaren (ikonen i adressfältet) och försök igen.',
  micMissing: 'Ingen mikrofon hittades.',
  micFailed: 'Det gick inte att starta mikrofonen.',
  speechFailed: 'Taligenkänningen misslyckades. Försök igen eller skriv meddelandet.',
  speechEmpty: 'Inget tal hördes. Försök igen lite närmare mikrofonen.',
  speechBudget: 'Dagens gräns för tal är nådd. Skriv meddelandet i stället.',
  audioUploadFailed: 'Inspelningen kunde inte laddas upp. Kontrollera anslutningen, eller ta bort inspelningen och skicka texten.',
  voiceMessage: 'Röstmeddelande',
  talkMode: 'Samtalsläge',
  talkModeHint: 'Klicka på mikrofonen och prata – det skickas direkt. Nya meddelanden läses upp på ditt språk.',
  talkModeOff: 'Stäng av',
  noVoice: 'Den här enheten saknar röst för {lang}, så meddelanden kan inte läsas upp. Installera en i systemets talinställningar.',
  readAloud: 'Läs upp',
  micHint: 'Klicka för att starta och stoppa – eller håll inne medan du pratar och släpp',
  releaseToSend: 'Släpp när du är klar · dra uppåt för att avbryta',
  releaseToCancel: 'Släpp för att avbryta',
  activeNow: 'aktiv nu',
  lastSeen: 'senast aktiv {time}',
  nActive: '{n} aktiva',
  typingOne: '{name} skriver…',
  typingMany: '{name} skriver…',
  listen: 'Lyssna',
  talkJoin: 'Slå på samtalsläget för mig också',
  talkShort: 'Samtal',
  searchShort: 'Sök',
  mentionSomeone: 'Nämn någon',
  mentionedYou: 'Nämnde dig',
  pin: 'Fäst',
  unpin: 'Lossa',
  pinned: 'Fäst',
  pinnedN: 'Fäst {i}/{n}',
  pinnedShowAll: 'Visa alla fästa',
  sys_pinned: '{actor} fäste ett meddelande.',
  sys_unpinned: '{actor} lossade ett fäst meddelande.',
  pushFailed: 'Pushnotiser gick inte att slå på (de fungerar inte i vissa nät, t.ex. Android i Kina). Använd e-postpåminnelser i stället.',
  appearance: 'Utseende',
  themeSystem: 'Automatiskt',
  themeLight: 'Ljust',
  themeDark: 'Mörkt',
  themeHint: 'Automatiskt följer telefonens eller datorns inställning. Gäller den här enheten.',
  soundNewMessages: 'Ljud vid nya meddelanden (på den här enheten)',
  soundTest: 'Spela upp',
  mixedTitle: 'Flera företag i samma chatt',
  mixedBody: 'Den här chatten skulle innehålla personer från {orgs}. De kommer att se varandra och alla meddelanden i chatten.',
  mixedConfirm: 'Fortsätt ändå',
  mixedAskAdmin: 'Bara en administratör kan skapa chattar med flera externa företag. Be Johan eller Jens.',
  sys_talk_on: '{actor} slog på samtalsläget – meddelanden pratas in och läses upp.',
  quote: 'Citera',
  imgTextButton: 'Översätt texten i bilden',
  imgTextTitle: 'Text i bilden',
  imgTextWorking: 'Läser bilden…',
  imgTextNone: 'Ingen läsbar text hittades i bilden.',
  imgTextFailed: 'Bilden kunde inte läsas. Försök igen.',
  summarise: 'Sammanfatta',
  sumChatTitle: 'Sammanfattning av chatten',
  sumDocTitle: 'Sammanfattning av {name}',
  sumWorking: 'Läser och sammanfattar…',
  sumDecisions: 'Överenskommet / beslutat',
  sumDates: 'Datum och tidsgränser',
  sumCommitments: 'Vem gör vad',
  sumOpen: 'Öppna frågor',
  sumTruncated: 'Bara den senaste delen togs med.',
  sumDisclaimer: 'Gjord av AI utifrån originaltexterna – kontrollera viktiga detaljer i chatten eller dokumentet.',
  sumNothing: 'Det finns inget att sammanfatta än.',
  sumNoText: 'PDF:en innehåller ingen text (den är troligen inskannade bilder), så den kan inte sammanfattas än.',
  sumUnreadable: 'PDF:en gick inte att läsa.',
  sumUnavailable: 'Sammanfattningar är inte tillgängliga just nu.',
  sumFailed: 'Sammanfattningen kunde inte göras. Försök igen.',
  sumPdf: 'Sammanfatta på mitt språk',
  receiptSent: 'Skickat',
  receiptRead: 'Läst',
  receiptReadAll: 'Läst av alla',
  receiptReadBy: 'Läst av {n} av {total}',
  diag: 'Anslutningstester',
  diagIntro: 'Resultat från sidan för anslutningstest. Skicka den här länken till en testare (inget konto behövs):',
  diagEmpty: 'Inga tester än.',
  diagCode: 'Kod',
  diagWho: 'Vem / enhet',
  diagFailures: 'Misslyckade steg',
  didYouMean: 'Menade du:',
  sendCorrected: 'Skicka rättat',
  sendAsIs: 'Skicka som det är',
  editText: 'Redigera',
  autoCorrected: 'Rättat innan det skickades: {list}',
  textCheckTitle: 'Stavningskontroll innan du skickar',
  textCheckAsk: 'Fråga mig när något ser fel ut (rekommenderas)',
  textCheckAuto: 'Rätta automatiskt',
  textCheckOff: 'Av',
  textCheckHint: 'Tydliga skrivfel fångas innan översättningen, så att mottagaren får rätt innebörd. Siffror, artikelnummer och namn ändras aldrig.',
  notifications: 'Aviseringar',
  pushOn: 'Slå på notiser på den här enheten',
  pushOff: 'Stäng av på den här enheten',
  pushIsOn: 'Du får en notis när ett nytt meddelande kommer och appen inte är öppen.',
  pushIsOff: 'Avstängt på den här enheten.',
  pushNeedsInstall: 'På iPhone/iPad: lägg först till appen på hemskärmen (Dela → Lägg till på hemskärmen), öppna den därifrån och slå sedan på notiser här.',
  pushDenied: 'Notiser är blockerade för den här sidan. Tillåt dem i webbläsarens eller systemets inställningar.',
  pushUnsupported: 'Den här webbläsaren kan inte visa notiser när appen är stängd. E-postpåminnelser fungerar ändå.',
  emailReminders: 'Mejla mig när meddelanden förblir olästa (ingen meddelandetext i mejlet)',
  emailNotConfigured: 'E-postpåminnelser är inte inställda på servern än.',
  notificationsHint: 'Notiser innehåller aldrig meddelandetext – bara vem som skrev och i vilken chatt. I Kina kan notiser till Android-telefoner utebli; där fungerar e-postpåminnelser.',
  suggestions: 'Förslag',
  suggestShort: 'Bättre översättning?',
  suggestTitle: 'Föreslå en bättre översättning',
  suggestYourVersion: 'Din översättning',
  suggestComment: 'Kommentar (frivillig)',
  suggestPrivacy: 'Meddelandet och ditt förslag visas för administratörerna, som avgör om det ska användas.',
  suggestSend: 'Skicka förslag',
  suggestThanks: 'Tack! En administratör går igenom förslaget. Godkända förslag förbättrar framtida översättningar av liknande meddelanden.',
  suggestFailed: 'Det gick inte att skicka förslaget.',
  suggestionsIntro: 'Rättelser som läsare har föreslagit. Godkända används som referensöversättningar för liknande meddelanden framöver. För ett enskilt ord eller en term, lägg in det i ordlistan i stället.',
  suggestApply: 'Ersätt även översättningen i chatten när jag godkänner',
  suggestionsEmpty: 'Inga förslag väntar.',
  suggestMachine: 'Maskinöversättning',
  suggestProposed: 'Förslag (du kan redigera innan du godkänner)',
  approve: 'Godkänn',
  reject: 'Avvisa',
  attach: 'Bifoga bild, skärmdump eller PDF (du kan också klistra in eller dra hit)',
  attachments: 'Bilagor',
  uploading: 'Laddar upp…',
  uploadFailed: 'Uppladdningen misslyckades. Kontrollera anslutningen och försök igen.',
  fileTypeNotAllowed: 'Bara bilder (PNG, JPEG, WebP, GIF) och PDF-filer kan skickas.',
  tooManyFiles: 'Högst {n} filer åt gången.',
  fileTooLarge: 'En fil är större än {size} och hoppades över.',
  download: 'Ladda ner',
  openImage: 'Öppna bilden',
  adminNotAllowed: 'Den här adressen får inte vara administratör. Bara adresser som serverns ägare har angett kan vara det (fyll i e-postadressen ovan).',
  emailTaken: 'Det finns redan ett konto med den här e-postadressen.',
  inviteFailed: 'Det gick inte att skapa inbjudan.',
  inviteCreated: 'Inbjudan skapad. Skicka länken via en kanal du litar på. Den fungerar en gång och går ut {date}.',
  copyLink: 'Kopiera länk',
  inviteQr: 'QR-kod för inbjudan',
  inviteLanguage: 'Inbjudningstext på',
  inviteText: 'Inbjudningstext',
  share: 'Dela…',
  copyText: 'Kopiera text',
  copyQr: 'Kopiera QR-bild',
  downloadQr: 'Ladda ner QR',
  copyImageFailed: 'Den här webbläsaren kan inte kopiera bilder – använd Ladda ner QR.',
  inviteShareHint: 'WeChat: skicka texten, eller QR-bilden (mottagaren håller inne på bilden → skanna), eller låt dem skanna från din skärm.',
  revoke: 'Återkalla',
  status_used: 'Använd av {name}',
  status_revoked: 'Återkallad',
  status_expired: 'Utgången',
  status_open: 'Öppen till {date}',
  disable: 'Inaktivera',
  enable: 'Aktivera',
  disabled: 'Inaktiverad',
  resetLink: 'Länk för nytt lösenord',
  resetCreated: 'Återställningslänk (en gång, 24 timmar):',
  todayChars: 'Tecken översatta i dag',
  monthCap: 'Månadsgräns',
  dailyCap: 'Dagsgräns',
  queue: 'Jobbkö',
  noPrivateAccess: 'Administratörer hanterar personer och kostnader. De kan inte läsa andras chattar.',
  sys_group_created: '{actor} skapade gruppen',
  sys_renamed: '{actor} döpte om gruppen till ”{title}”',
  sys_members_added: '{actor} lade till {users}',
  sys_member_removed: '{actor} tog bort {users}',
  sys_member_left: '{actor} lämnade gruppen',
  someone: 'Någon',
  error_generic: 'Något gick fel ({code}).',
  emoji_smileys: 'Ansikten',
  emoji_gestures: 'Gester',
  emoji_work: 'Arbete',
  emoji_symbols: 'Symboler',
  emoji_flags: 'Flaggor',
  next: 'Nästa',
  back: 'Tillbaka',
  more: 'Mer',
  settingsChat: 'Språk och skrivande',
  settingsSecurity: 'Inloggning och säkerhet',
  settingsPrivacy: 'Integritet och data',
  dlShort: 'Appar',
  dlNone: 'Inga appar är uppladdade än.',
  downloadApp: 'Ladda ner appen',
  dlTitle: 'Appar för dator och telefon',
  dlIntro: 'Samma chatt som här, med notiser även när fönstret är stängt.',
  dlMacArm: 'Mac (Apple-chip, M1 och senare)',
  dlMacIntel: 'Mac (Intel)',
  dlWindowsHint: 'Om Windows säger "Windows skyddade datorn": klicka Mer information → Kör ändå.',
  dlAndroidHint: 'Öppna filen på telefonen och tillåt installation från den appen en gång.',
  dlIphoneHint: 'Via TestFlight: be din administratör om en inbjudan.',
  dlButton: 'Ladda ner',
  later: 'Senare',
  appUpdateReady: 'En ny version av T-Lián ({v}) är klar.',
  appUpdateRestart: 'Starta om och uppdatera',
  appUpdate: 'En ny version av appen finns ({v}). Ladda ner och installera den över den gamla.',
  dlForThis: 'För den här enheten',
  dlOthers: 'Andra versioner',
  dlWinArm: 'Windows (ARM)',
  dlWinArmHint: 'Bara för Windows-datorer med ARM-chip (t.ex. Snapdragon). De flesta datorer behöver den ovanför.',
  takePhoto: 'Ta foto',
};

const zhHans: Dict = {
  appName: 'T-Lián',
  signIn: '登录',
  signingIn: '正在登录…',
  email: '邮箱',
  password: '密码',
  newPassword: '新密码',
  currentPassword: '当前密码',
  passwordHint: '至少 10 个字符。',
  invalidCredentials: '邮箱或密码错误。',
  accountLocked: '尝试次数过多。请等待 15 分钟后再试。',
  networkError: '无法连接服务器。请检查网络后重试。',
  inviteOnly: '账号仅通过邀请创建。请向管理员索取邀请链接。',
  forgotPassword: '忘记密码？',
  forgotTitle: '忘记密码',
  forgotIntro: '请输入您登录使用的电子邮箱，我们将帮助您设置新密码。',
  forgotSend: '发送',
  forgotSentEmail: '如果该邮箱已注册，含链接的邮件正在发送中。链接一小时内有效。也请检查垃圾邮件文件夹。',
  forgotSentAdmin: '已通知管理员。管理员会给您发送设置新密码的链接。',
  backToSignIn: '返回登录',
  or: '或',
  mfa: '两步登录',
  mfaIntro: '输入密码后，还需输入手机应用（Google Authenticator、Microsoft Authenticator 或 iPhone"密码"应用）中的 6 位验证码。',
  mfaAdminTip: '建议管理员开启。',
  mfaTurnOn: '开启两步登录',
  mfaTurnOff: '关闭',
  mfaOn: '已开启',
  mfaScan: '用手机上的应用扫描二维码，然后输入显示的 6 位数字。',
  mfaQrAlt: '验证器应用的二维码',
  mfaManual: '无法扫描？请在应用中输入此密钥：',
  mfaCode: '应用中的验证码',
  mfaConfirm: '确认',
  mfaCodesTitle: '备用码',
  mfaCodesHint: '手机不在身边时，每个备用码可使用一次。请妥善保存（例如保存在密码管理器中）。仅显示这一次。',
  mfaCodesSaved: '我已保存',
  mfaCodesLeft: '剩余 {n} 个备用码',
  mfaTitle: '两步登录',
  mfaPrompt: '请输入手机验证器应用中的 6 位验证码。',
  mfaRecoveryHint: '手机不在身边？请输入一个备用码。',
  mfaCodeInvalid: '验证码不正确。请等待应用中的下一个验证码后重试。',
  mfaTicketInvalid: '用时过长或尝试次数过多。请重新登录。',
  mfaTag: '两步',
  mfaAdminReset: '关闭两步登录',
  mfaAdminResetConfirm: '要为 {name} 关闭两步登录吗？仅在对方手机和备用码都丢失时这样做。',
  desktopNotify: '在此电脑上显示通知',
  chatFiles: '文件',
  chatFilesNone: '此聊天中还没有分享图片或 PDF。',
  showInChat: '在聊天中查看',
  noOneToAdd: '您可以添加的人都已在此群组中。',
  noOneToAddAdmin: 'T-Lián 中的所有人都已在此群组中。请在"管理 → 邀请"中邀请更多人。',
  signInPasskey: '使用通行密钥登录',
  passkeyInvalid: '无法使用该通行密钥。请用密码登录，然后在"设置"中重新添加通行密钥。',
  passkeys: '通行密钥',
  passkeysIntro: '使用面容 ID、触控 ID、Windows Hello 或手机登录，无需输入密码。密码仍然可用。',
  addPasskey: '在此设备上添加通行密钥',
  removePasskey: '删除',
  faceIdSignIn: '使用面容 ID 登录',
  faceIdIntro: '使用面容 ID 登录和解锁应用，无需输入密码。密码仍然可用。',
  faceIdAdd: '开启面容 ID 登录',
  faceIdToggle: '在这部 iPhone 上使用面容 ID 登录',
  faceIdOffer: '使用面容 ID 登录？',
  faceIdYes: '开启',
  bioTitle: '指纹或面部解锁',
  bioIntro: '使用指纹或面部登录和解锁应用，无需输入密码。密码仍然可用。',
  bioToggle: '在这部手机上使用指纹或面部登录',
  bioSignIn: '使用指纹登录',
  bioOffer: '使用指纹或面部登录？',
  faceIdNo: '不用了',
  faceIdRevoked: '面容 ID 登录已关闭（已在所有设备退出）。请用密码登录，然后在"设置"中重新开启。',
  faceIdFailed: '无法开启面容 ID，请重试。',
  faceIdOfferHint: '开启后，应用在后台超过 5 分钟会自动锁定，看一眼或按一下手指即可打开。可随时在"设置 → 安全"中更改。',
  lockAfter: '多久后锁定应用',
  lockNow: '立即',
  lockMinutes: '{n} 分钟',
  lockHour: '1 小时',
  lockNever: '从不',
  lockTitle: 'T-Lián 已锁定',
  versionApp: '应用',
  versionWeb: '网页',
  lockUnlockFace: '使用面容 ID 解锁',
  lockUnlockBio: '使用指纹解锁',
  lockUsePassword: '改用密码登录',
  passkeyLastUsed: '上次使用：{when}',
  passkeyNever: '尚未使用',
  passkeySynced: '已同步',
  passkeysUnsupported: '此浏览器不支持通行密钥。',
  passkeysDesktop: '通行密钥可在网页浏览器中使用。在 T-Lián 应用中请使用密码登录。',
  resetRequested: '已申请新密码',
  joinTitle: '加入 {org}',
  joinIntro: '请选择阅读消息的语言。之后可以随时修改。',
  displayName: '您的姓名',
  uiLanguage: '界面语言',
  nativeLanguage: '母语',
  nativeLanguageHint: '应用界面使用此语言显示，所有消息都会翻译成此语言。',
  change: '更改',
  readingLanguage: '消息阅读语言',
  readingLanguageHint: '其他语言的消息会翻译成此语言，原文随时一键可见。',
  createAccount: '创建账号',
  inviteInvalid: '此邀请链接已过期或已被使用。',
  resetTitle: '设置新密码',
  resetDone: '密码已修改。请使用新密码登录。',
  resetInvalid: '此重置链接已过期或已被使用。',
  savePassword: '保存密码',
  conversations: '聊天',
  newChat: '新聊天',
  noConversations: '还没有聊天。可以与同事或供应商开始一个。',
  noResults: '没有匹配的消息。',
  selectConversation: '请在左侧选择聊天，或开始新的聊天。',
  directMessage: '私聊',
  group: '群组',
  groupName: '群组名称',
  addPeople: '添加成员',
  createGroup: '创建群组',
  startChat: '开始聊天',
  filterPeople: '查找联系人',
  members: '{n} 位成员',
  you: '你',
  loadEarlier: '加载更早的消息',
  beginning: '聊天从这里开始。',
  typeMessage: '输入消息',
  send: '发送',
  sending: '发送中…',
  notSent: '未发送',
  retry: '重试',
  discard: '丢弃',
  reply: '回复',
  react: '添加表情回应',
  edit: '编辑',
  delete: '删除',
  copy: '复制文字',
  copied: '已复制',
  cancel: '取消',
  save: '保存',
  close: '关闭',
  replyingTo: '回复 {name}',
  editing: '正在编辑消息',
  edited: '已编辑',
  deleted: '消息已删除',
  confirmDelete: '为所有人删除这条消息？',
  writingIn: '书写语言',
  autoDetect: '自动识别',
  showOriginal: '查看原文',
  showTranslation: '查看译文',
  translatedFrom: '译自{lang}',
  originalIn: '原文（{lang}）',
  translating: '翻译中…',
  translationRetrying: '翻译延迟，正在重试…',
  translationFailed: '翻译失败',
  translationDisabled: '翻译已关闭',
  budgetExceeded: '今日翻译额度已用完',
  retranslate: '重新翻译',
  review: '请核对此译文',
  askClarification: '请求澄清',
  clarifyGeneric: '您能确认一下这里的意思吗？我想确保我理解正确。',
  mockNotice: '演示模式：译文为模拟内容，并非真实翻译。',
  flag_number_missing: '原文中的数字缺失：{d}',
  flag_number_added: '译文出现了原文没有的数字：{d}',
  flag_code_missing: '代码或零件号被改动：{d}',
  flag_url_missing: '链接或地址被改动：{d}',
  flag_emoji_mismatch: '表情与原文不一致',
  flag_negation_missing: '原文中的否定可能丢失',
  flag_negation_added: '译文可能多了否定',
  flag_condition_missing: '原文中的“如果”条件可能丢失',
  flag_weekday_mismatch: '星期与原文不一致',
  flag_model_uncertain: '译者认为此处有歧义：{d}',
  detectedLanguage: '语言为自动识别',
  mixedLanguage: '混合语言',
  connConnecting: '正在连接…',
  connPolling: '已以兼容模式连接，消息可能延迟几秒。',
  connOffline: '网络已断开。恢复连接后会自动发送您写的消息。',
  settings: '设置',
  profile: '个人资料',
  enterToSend: '按 Enter 发送（Shift+Enter 或 Alt/Option+Enter 换行）',
  changePassword: '修改密码',
  passwordChanged: '密码已修改，其他设备已退出登录。',
  exportData: '下载我的数据',
  exportHint: '您的资料、所在的聊天以及您写过的所有消息（JSON 格式）。',
  signOut: '退出登录',
  signOutEverywhere: '在所有设备上退出',
  saved: '已保存',
  groupInfo: '群组信息',
  chatInfo: '聊天信息',
  rename: '重命名',
  leaveGroup: '退出群组',
  confirmLeave: '确定退出群组？退出后将无法查看其消息。',
  remove: '移除',
  confirmRemove: '将 {name} 移出群组？对方将无法再查看群消息。',
  makeAdmin: '设为管理员',
  removeAdmin: '取消管理员',
  role_owner: '群主',
  role_admin: '管理员',
  role_member: '成员',
  askHistory: '是否允许 {name} 查看此群组之前的消息？',
  historyOnlyNew: '仅新消息',
  historyAll: '全部历史记录',
  historyNote: '后加入的成员只能看到加入之后的消息。',
  add: '添加',
  admin: '管理',
  invites: '邀请',
  users: '人员',
  usage: '用量',
  costs: "费用",
  costEstimateTitle: "AI 用量（估算）",
  costEstimateNote: "根据标价和每次调用的用量估算。免费额度覆盖时也会显示，因此实际账单可能更低。",
  costToday: "今天",
  costThisMonth: "本月",
  costTrackedSince: "自 {date} 起统计",
  costNothingYet: "尚无统计。从本次更新后的第一次 AI 调用开始统计。",
  costByFeature: "按功能",
  costByCompany: "按公司（发送或点击的人）",
  costCalls: "调用",
  costUsed: "用量",
  costUnpriced: "部分模型没有价格，合计为最低值。",
  costFtranslate: "翻译",
  costFcheck: "拼写检查",
  costFsummary: "摘要",
  costFimage_text: "图片文字",
  costFtranscribe: "语音输入",
  costFtts: "朗读",
  costUnknownOrg: "未知",
  billTitle: "阿里云账单",
  billNote: "阿里云已计费的金额，包含所有服务（服务器、文件、AI）。会延迟几个小时；免费额度覆盖的部分显示为 0。",
  billNotConfigured: "服务器上没有账单密钥。请在 .env 中设置 BILLING_ACCESS_KEY_ID 和 BILLING_ACCESS_KEY_SECRET（具有 AliyunBSSReadOnlyAccess 策略的 RAM 用户）。",
  billNoPermission: "该密钥无权读取账单。请为其 RAM 用户添加 AliyunBSSReadOnlyAccess 策略。",
  billError: "无法获取账单（{code}）。",
  billBalance: "账户余额",
  billRefresh: "刷新",
  billTotal: "合计",
  billEmpty: "没有费用。",
  costChars: "字符",
  consentTitle: "隐私与同意",
  consentIntro: "继续之前，请阅读我们如何处理您的信息并表示同意。您可以随时通过删除账号撤回同意。",
  acceptAndContinue: "同意并继续",
  declineSignOut: "暂不同意 – 退出登录",
  privacy: "隐私",
  privacyShow: "查看隐私声明",
  deleteAccount: "删除我的账号",
  deleteAccountHint: "您发送的所有消息将对所有人清空，您将退出所有聊天，账号将被匿名化。此操作无法撤销。请输入密码确认。",
  deleteAccountConfirm: "永久删除我的账号",
  glossaryStarterDone: "已添加 {added} 个术语，更新了 {updated} 个之前的建议。您自己修改过的术语保持不变。",
  searchResults: "搜索结果",
  searchTooOld: "这条消息太早，无法在此打开。",
  showMore: "显示更多",
  searchInChat: "在此聊天中搜索",
  searchPlaceholder: "搜索所有聊天 (⌘K)",
  glossary: "术语表",
  glossaryIntro: "公司术语，用于所有翻译。翻译时始终使用已批准的术语，如未使用会提醒读者。标记为“不翻译”的名称将保持原样。",
  glossaryNote: "备注",
  keepOriginal: "不翻译（名称或缩写）",
  keepOriginalShort: "不翻译",
  glossarySynonymsHint: "同一事物的多个词用 ; 分隔——第一个用于翻译，其余也会被识别。",
  glossarySearch: "搜索术语",
  glossaryPaste: "从 Excel 粘贴",
  glossaryPasteHint: "从 Excel/Numbers 复制行，列顺序：Svenska、简体中文、English、备注、不翻译 (x)。同一单元格内的同义词用 ; 分隔。",
  glossaryImport: "导入 {n} 行",
  glossaryStarter: "添加推荐的音视频术语",
  glossaryImported: "已添加 {added} 个术语，{skipped} 个已存在。",
  glossaryEmpty: "暂无术语。",
  glossaryConfirmDelete: "删除此术语？",
  flag_glossary_term_missing: "未使用公司术语：{d}",
  createInvite: '创建邀请',
  organization: '公司',
  newOrganization: '新公司…',
  newOrganizationName: '公司名称',
  emailOptional: '邮箱（可选，邀请仅限此地址使用）',
  asAdmin: '可管理此应用',
  inviteSendEmail: '同时通过邮件发送邀请，语言：',
  inviteMailed: '邀请已通过邮件发送至 {to}。',
  inviteMailFailed: '无法发送邮件至 {to}，请自行发送下方链接。',
  micStart: '语音输入（说完再点一次）',
  micStop: '完成',
  listening: '正在听…请说话',
  transcribing: '正在把语音转换成文字…',
  sendWithAudio: '同时发送我的语音（{d}），对方可以收听',
  discardRecording: '删除录音',
  micDenied: '麦克风被阻止。请在浏览器中允许本网站使用麦克风（地址栏中的图标），然后重试。',
  micMissing: '未找到麦克风。',
  micFailed: '无法启动麦克风。',
  speechFailed: '语音识别失败。请重试或直接输入文字。',
  speechEmpty: '没有听到语音。请靠近麦克风再试一次。',
  speechBudget: '今天的语音使用量已达上限。请直接输入文字。',
  audioUploadFailed: '录音上传失败。请检查网络，或删除录音后只发送文字。',
  voiceMessage: '语音消息',
  talkMode: '对讲模式',
  talkModeHint: '点击麦克风说话，说完立即发送。新消息会用您的语言朗读出来。',
  talkModeOff: '关闭',
  noVoice: '本设备没有{lang}语音，无法朗读消息。请在系统的语音设置中安装。',
  readAloud: '朗读',
  micHint: '点击开始和结束，或按住说话、松开结束',
  releaseToSend: '松开结束 · 上滑取消',
  releaseToCancel: '松开取消',
  activeNow: '在线',
  lastSeen: '最后在线 {time}',
  nActive: '{n} 人在线',
  typingOne: '{name} 正在输入…',
  typingMany: '{name} 正在输入…',
  listen: '收听',
  talkJoin: '我也开启对讲模式',
  talkShort: '对讲',
  searchShort: '搜索',
  mentionSomeone: '提到某人',
  mentionedYou: '提到了你',
  pin: '置顶',
  unpin: '取消置顶',
  pinned: '已置顶',
  pinnedN: '置顶 {i}/{n}',
  pinnedShowAll: '查看全部置顶',
  sys_pinned: '{actor} 置顶了一条消息。',
  sys_unpinned: '{actor} 取消了一条置顶消息。',
  pushFailed: '无法开启推送通知（在部分网络中不可用，例如中国大陆的安卓手机）。请改用邮件提醒。',
  appearance: '外观',
  themeSystem: '自动',
  themeLight: '浅色',
  themeDark: '深色',
  themeHint: '"自动"跟随手机或电脑的设置。仅对本设备有效。',
  soundNewMessages: '新消息提示音（本设备）',
  soundTest: '试听',
  mixedTitle: '同一聊天中有多家公司',
  mixedBody: '此聊天将包含来自 {orgs} 的人员。他们将看到彼此以及聊天中的所有消息。',
  mixedConfirm: '仍然继续',
  mixedAskAdmin: '只有管理员才能创建包含多家外部公司的聊天。',
  sys_talk_on: '{actor} 开启了对讲模式——消息用语音输入并朗读出来。',
  quote: '引用',
  imgTextButton: '翻译图片中的文字',
  imgTextTitle: '图片中的文字',
  imgTextWorking: '正在读取图片…',
  imgTextNone: '图片中没有找到可读的文字。',
  imgTextFailed: '无法读取图片，请重试。',
  summarise: '总结',
  sumChatTitle: '聊天总结',
  sumDocTitle: '{name} 的总结',
  sumWorking: '正在阅读和总结…',
  sumDecisions: '已商定 / 已决定',
  sumDates: '日期和截止时间',
  sumCommitments: '谁负责什么',
  sumOpen: '待解决的问题',
  sumTruncated: '只包含了最近的部分。',
  sumDisclaimer: '由 AI 根据原文生成，重要细节请在聊天或文档中核对。',
  sumNothing: '还没有可以总结的内容。',
  sumNoText: '此 PDF 不含文字（可能是扫描图片），暂时无法总结。',
  sumUnreadable: '无法读取此 PDF。',
  sumUnavailable: '暂时无法使用总结功能。',
  sumFailed: '无法生成总结，请重试。',
  sumPdf: '用我的语言总结',
  receiptSent: '已发送',
  receiptRead: '已读',
  receiptReadAll: '全部已读',
  receiptReadBy: '{total} 人中 {n} 人已读',
  diag: '连接测试',
  diagIntro: '连接测试页面的结果。把这个链接发给测试人员（无需账号）：',
  diagEmpty: '还没有测试。',
  diagCode: '代码',
  diagWho: '人员 / 设备',
  diagFailures: '失败的步骤',
  didYouMean: '您是不是想说：',
  sendCorrected: '发送修改后的',
  sendAsIs: '按原样发送',
  editText: '编辑',
  autoCorrected: '发送前已自动修改：{list}',
  textCheckTitle: '发送前检查拼写',
  textCheckAsk: '发现明显错误时问我（推荐）',
  textCheckAuto: '自动修改',
  textCheckOff: '关闭',
  textCheckHint: '翻译前会发现明显的错别字，让对方理解正确的意思。数字、型号和名称永远不会被修改。',
  notifications: '通知',
  pushOn: '在此设备上开启通知',
  pushOff: '在此设备上关闭',
  pushIsOn: '当有新消息且应用未打开时，您会收到通知。',
  pushIsOff: '此设备上已关闭。',
  pushNeedsInstall: 'iPhone/iPad：请先把应用添加到主屏幕（分享 → 添加到主屏幕），从主屏幕打开后再在这里开启通知。',
  pushDenied: '此网站的通知已被阻止。请在浏览器或系统设置中允许。',
  pushUnsupported: '此浏览器无法在应用关闭时显示通知。邮件提醒仍然有效。',
  emailReminders: '有未读消息时发邮件提醒我（邮件中不含消息内容）',
  emailNotConfigured: '服务器尚未设置邮件提醒。',
  notificationsHint: '通知中从不包含消息内容，只有发送人和聊天名称。在中国大陆，安卓手机可能收不到通知，邮件提醒可以正常使用。',
  suggestions: '建议',
  suggestShort: '更好的翻译？',
  suggestTitle: '建议更好的翻译',
  suggestYourVersion: '您的翻译',
  suggestComment: '备注（可选）',
  suggestPrivacy: '这条消息和您的建议会显示给管理员，由他们决定是否采用。',
  suggestSend: '发送建议',
  suggestThanks: '谢谢！管理员会审核您的建议。被采纳的建议会改进以后类似消息的翻译。',
  suggestFailed: '无法发送建议。',
  suggestionsIntro: '读者提出的修改建议。被批准的建议将作为今后类似消息的参考译文。单个词语或术语请添加到术语表。',
  suggestApply: '批准时同时替换聊天中的译文',
  suggestionsEmpty: '没有待处理的建议。',
  suggestMachine: '机器翻译',
  suggestProposed: '建议译文（批准前可编辑）',
  approve: '批准',
  reject: '拒绝',
  attach: '添加图片、截图或 PDF（也可以粘贴或拖入）',
  attachments: '附件',
  uploading: '正在上传…',
  uploadFailed: '上传失败。请检查网络后重试。',
  fileTypeNotAllowed: '只能发送图片（PNG、JPEG、WebP、GIF）和 PDF 文件。',
  tooManyFiles: '一次最多 {n} 个文件。',
  fileTooLarge: '有文件超过 {size}，已跳过。',
  download: '下载',
  openImage: '打开图片',
  adminNotAllowed: '该邮箱不能成为管理员。只有服务器所有者指定的邮箱才可以（请在上方填写邮箱）。',
  emailTaken: '该邮箱已有账号。',
  inviteFailed: '无法创建邀请。',
  inviteCreated: '邀请已创建。请通过可信渠道发送此链接。链接只能使用一次，{date} 过期。',
  copyLink: '复制链接',
  inviteQr: '邀请二维码',
  inviteLanguage: '邀请文字语言',
  inviteText: '邀请文字',
  share: '分享…',
  copyText: '复制文字',
  copyQr: '复制二维码图片',
  downloadQr: '下载二维码',
  copyImageFailed: '此浏览器无法复制图片，请使用"下载二维码"。',
  inviteShareHint: '微信：发送文字，或发送二维码图片（长按 → 识别），或让对方扫描您屏幕上的二维码。',
  revoke: '撤销',
  status_used: '已由 {name} 使用',
  status_revoked: '已撤销',
  status_expired: '已过期',
  status_open: '有效期至 {date}',
  disable: '停用',
  enable: '启用',
  disabled: '已停用',
  resetLink: '密码重置链接',
  resetCreated: '重置链接（仅可使用一次，24 小时内有效）：',
  todayChars: '今日翻译字符数',
  monthCap: '每月上限',
  dailyCap: '每日上限',
  queue: '任务队列',
  noPrivateAccess: '管理员负责人员与费用管理，无法查看他人的聊天内容。',
  sys_group_created: '{actor} 创建了群组',
  sys_renamed: '{actor} 将群组改名为“{title}”',
  sys_members_added: '{actor} 添加了 {users}',
  sys_member_removed: '{actor} 移除了 {users}',
  sys_member_left: '{actor} 退出了群组',
  someone: '某人',
  error_generic: '出现错误（{code}）。',
  emoji_smileys: '表情',
  emoji_gestures: '手势',
  emoji_work: '工作',
  emoji_symbols: '符号',
  emoji_flags: '旗帜',
  next: '下一步',
  back: '返回',
  more: '更多',
  settingsChat: '语言与输入',
  settingsSecurity: '登录与安全',
  settingsPrivacy: '隐私与数据',
  dlShort: '应用',
  dlNone: '尚未上传任何应用。',
  downloadApp: '下载应用',
  dlTitle: '电脑和手机应用',
  dlIntro: '与这里相同的聊天，窗口关闭时也能收到通知。',
  dlMacArm: 'Mac（Apple 芯片，M1 及更新）',
  dlMacIntel: 'Mac（Intel）',
  dlWindowsHint: '如果 Windows 提示"Windows 已保护你的电脑"：点击"更多信息"→"仍要运行"。',
  dlAndroidHint: '在手机上打开该文件，并允许从该应用安装一次。',
  dlIphoneHint: '通过 TestFlight：请向管理员索取邀请。',
  dlButton: '下载',
  later: '稍后',
  appUpdateReady: 'T-Lián 新版本（{v}）已准备好。',
  appUpdateRestart: '重新启动并更新',
  appUpdate: '应用有新版本（{v}）。请下载并覆盖安装旧版本。',
  dlForThis: '适用于此设备',
  dlOthers: '其他版本',
  dlWinArm: 'Windows（ARM）',
  dlWinArmHint: '仅适用于 ARM 芯片的 Windows 笔记本（如骁龙）。大多数电脑请用上面那个。',
  takePhoto: '拍照',
};

const DICTS: Record<string, Dict> = { en, 'sv-SE': sv, 'zh-Hans': zhHans };

export function translate(locale: string, key: MsgKey, vars: Record<string, string | number> = {}): string {
  const dict = DICTS[locale] ?? en;
  const template = dict[key] ?? en[key] ?? key;
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));
}

export function browserUiLocale(): string {
  return pickUiLocale(navigator.languages ?? [navigator.language]);
}

/** Signed-in: the user's setting. Before sign-in: the language picked on this device, else the browser's. */
export function useUiLocale(): string {
  const mine = useStore((s) => s.me?.uiLocale);
  const device = useStore((s) => s.nativeLocale);
  return mine ?? (device ? (device === 'zh-Hant' ? 'zh-Hans' : device) : browserUiLocale());
}

export function useT(): (key: MsgKey, vars?: Record<string, string | number>) => string {
  const locale = useUiLocale();
  return (key, vars) => translate(locale, key, vars);
}

/** Language names shown in the UI language. */
const LANG_NAMES: Record<string, Record<string, string>> = {
  en: { 'sv-SE': 'Swedish', 'zh-Hans': 'Chinese (Simplified)', 'zh-Hant': 'Chinese (Traditional)', en: 'English' },
  'sv-SE': { 'sv-SE': 'svenska', 'zh-Hans': 'kinesiska (förenklad)', 'zh-Hant': 'kinesiska (traditionell)', en: 'engelska' },
  'zh-Hans': { 'sv-SE': '瑞典语', 'zh-Hans': '简体中文', 'zh-Hant': '繁体中文', en: '英语' },
};

export function languageName(uiLocale: string, locale: string): string {
  return LANG_NAMES[uiLocale]?.[locale] ?? LANG_NAMES.en![locale] ?? locale;
}

/** Short seal glyph for a content language. */
export function seal(locale: string): string {
  switch (locale) {
    case 'sv-SE':
      return 'Sv';
    case 'zh-Hans':
      return '中';
    case 'zh-Hant':
      return '繁';
    case 'en':
      return 'En';
    default:
      return locale.slice(0, 2);
  }
}

export function formatTime(iso: string, uiLocale: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tag = uiLocale === 'sv-SE' ? 'sv-SE' : uiLocale === 'zh-Hans' ? 'zh-CN' : 'en-GB';
  return sameDay
    ? d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(tag, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' });
}

/** Clock time only — inside a chat the day separator already says which day. */
export function formatClock(iso: string, uiLocale: string): string {
  const tag = uiLocale === 'sv-SE' ? 'sv-SE' : uiLocale === 'zh-Hans' ? 'zh-CN' : 'en-GB';
  return new Date(iso).toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' });
}

export function formatDay(iso: string, uiLocale: string): string {
  const tag = uiLocale === 'sv-SE' ? 'sv-SE' : uiLocale === 'zh-Hans' ? 'zh-CN' : 'en-GB';
  return new Date(iso).toLocaleDateString(tag, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
